import { useState, useEffect } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';

export const SearchFilters = ({ filters, onFilterChange, filterOptions, onClearFilters }) => {
  const [localSearch, setLocalSearch] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== filters.search) {
        onFilterChange('search', localSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, filters.search, onFilterChange]);

  const hasActiveFilters = filters.search || filters.estado || filters.rango || 
    filters.especialidad || filters.estatus;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="search-filters">
      {/* Search Input */}
      <div className="mb-6">
        <Label htmlFor="search" className="text-sm font-medium text-slate-700 mb-2 block">
          Buscar
        </Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            id="search"
            type="text"
            placeholder="Nombre o número de registro"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-10 h-10 border-slate-200 focus:ring-slate-900"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Estado */}
      <div className="mb-6">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">
          Estado
        </Label>
        <Select
          value={filters.estado || 'all'}
          onValueChange={(value) => onFilterChange('estado', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-10 border-slate-200" data-testid="estado-select">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {filterOptions?.estados?.map((estado) => (
              <SelectItem key={estado} value={estado}>
                {estado}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Rango */}
      <div className="mb-6">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">
          Rango
        </Label>
        <Select
          value={filters.rango || 'all'}
          onValueChange={(value) => onFilterChange('rango', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-10 border-slate-200" data-testid="rango-select">
            <SelectValue placeholder="Todos los rangos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los rangos</SelectItem>
            {filterOptions?.rangos?.map((rango) => (
              <SelectItem key={rango} value={rango}>
                {rango}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Especialidad */}
      <div className="mb-6">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">
          Especialidad
        </Label>
        <Select
          value={filters.especialidad || 'all'}
          onValueChange={(value) => onFilterChange('especialidad', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="h-10 border-slate-200" data-testid="especialidad-select">
            <SelectValue placeholder="Todas las especialidades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las especialidades</SelectItem>
            {filterOptions?.especialidades?.map((esp) => (
              <SelectItem key={esp} value={esp}>
                {esp}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Estatus */}
      <div className="mb-6">
        <Label className="text-sm font-medium text-slate-700 mb-3 block">
          Estatus
        </Label>
        <RadioGroup
          value={filters.estatus || 'all'}
          onValueChange={(value) => onFilterChange('estatus', value === 'all' ? '' : value)}
          className="space-y-2"
          data-testid="estatus-radio-group"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="all" id="estatus-all" />
            <Label htmlFor="estatus-all" className="text-sm text-slate-600 cursor-pointer">
              Todos
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Activo" id="estatus-activo" />
            <Label htmlFor="estatus-activo" className="text-sm text-slate-600 cursor-pointer">
              Activo
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Suspendido" id="estatus-suspendido" />
            <Label htmlFor="estatus-suspendido" className="text-sm text-slate-600 cursor-pointer">
              Suspendido
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Baja" id="estatus-baja" />
            <Label htmlFor="estatus-baja" className="text-sm text-slate-600 cursor-pointer">
              Baja
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="outline"
          size="sm"
          onClick={onClearFilters}
          className="w-full text-slate-600 border-slate-200 hover:bg-slate-50"
          data-testid="clear-filters-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Limpiar filtros
        </Button>
      )}
    </div>
  );
};
